// src/Components/conferencePages/Submission.js
import React from 'react';
import {
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  Download,
} from 'lucide-react';
import styles from './Submission.module.css';

export default function Submission() {
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Hero Section */}
        <div className={styles.heroSection}>
          <h1 className={styles.sectionTitle}>Paper Submission</h1>
          <p className={styles.sectionSubtitle}>
            Submit your research contributions following our comprehensive guidelines
          </p>
        </div>

        {/* Main Content Grid */}
        <div className={styles.contentGrid}>
          {/* Submission Process */}
          <div className={styles.submissionProcess}>
            <h2 className={styles.sectionTitle}>
              <Upload size={24} />
              Submission Process
            </h2>

            <div className={styles.processSteps}>
              {[
                {
                  number: 1,
                  title: 'Prepare Your Paper',
                  description:
                    'Format your paper according to the given template and our formatting requirements (see below).',
                },
                {
                  number: 2,
                  title: 'Review Guidelines',
                  description:
                    'Ensure you follow all formatting and submission rules before submitting your work.',
                },
                {
                  number: 3,
                  title: 'Submit Paper',
                  description:
                    'Upload your paper through our submission system or email. Include all required information.',
                },
                {
                  number: 4,
                  title: 'Await Review',
                  description:
                    'Your submission will be reviewed, and you will receive a decision within 2–3 weeks.',
                },
              ].map((step) => (
                <div className={styles.stepItem} key={step.number}>
                  <div className={styles.stepNumber}>{step.number}</div>
                  <div className={styles.stepContent}>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.actionButtons}>
              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&to=icetmcp2026@gmail.com"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.btnAccent}
              >
                <Upload size={16} />
                Email Submission
              </a>
              <p style={{ marginTop: '12px' }}>(OR)</p>
              <p style={{ fontSize: '15px', marginTop: '12px' }}><span style={{ fontWeight: 'bold' }}>Email : </span> icetmcp2026@gmail.com</p>
            </div>
          </div>

          {/* Submission Requirements */}
          <div className={styles.requirements}>
            <h2 className={styles.sectionTitle}>
              <FileText size={24} />
              Formatting Specifications
            </h2>

            <div className={styles.requirementsList}>
              {[
                {
                  label: 'Font',
                  value: 'Times New Roman, 10-point',
                },
                {
                  label: 'Layout',
                  value: 'Double column, single spaced',
                },
                {
                  label: 'Page Length',
                  value: '4–6 pages (maximum 3,500 words)',
                },
                {
                  label: 'Margins',
                  value:
                    'Bottom: 2.5cm, 1.5cm for all other sides',
                },
                {
                  label: 'Abstract',
                  value: 'Maximum 250 words',
                },
                {
                  label: 'Keywords',
                  value: 'Include 4–6 keywords',
                },
                {
                  label: 'Headers & Footers',
                  value: 'Do not include (added by conference team)',
                },
                {
                  label: 'Page Numbers',
                  value: 'Do not include (added by conference team)',
                },
              ].map((req, index) => (
                <div className={styles.requirementItem} key={index}>
                  <CheckCircle size={20} />
                  <div>
                    <strong>{req.label}:</strong>
                    <p>{req.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.downloadSection}>
              <h3>Templates & Resources</h3>
              <div className={styles.downloadLinks}>
                <a href={process.env.PUBLIC_URL + "/assets/ICETMCP-2026 (Brochure).pdf"} download='ICETMCP-2026 (Brochure)' className={styles.downloadBtn}>
                  <Download size={16} />
                  Brochure
                </a>
                <a href={process.env.PUBLIC_URL + "/assets/Conference-Template.docx"} download='Conference-Template.docx' className={styles.downloadBtn}>
                  <Download size={16} />
                  Ms Word Template
                </a>
                <a href={process.env.PUBLIC_URL + "/assets/Submission-Guidelines.pdf"} download='Submission-Guidelines.pdf' className={styles.downloadBtn}>
                  <Download size={16} />
                  Submission Guidelines
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Review Process Section */}
        <div className={styles.reviewProcess}>
          <h2 className={styles.sectionTitle}>Review Process</h2>

          <div className={styles.reviewGrid}>
            {[
              {
                number: '1.',
                title: 'Initial Screening',
                text: 'All submitted papers will be checked for plagiarism and compliance with formatting guidelines.',
              },
              {
                number: '2.',
                title: 'Peer Review',
                text: 'Reviewed by International Technical Committee members.',
              },
              {
                number: '3.',
                title: 'Decision',
                text: 'Authors receive Accept / Revision / Reject decision within 2–3 weeks.',
              },
              {
                number: '4.',
                title: 'Publication',
                text: 'All accepted papers will be included and published in conference proceedings with ISBN (978-81-991869-0-3). Selected papers will be published in scopus indexed journals after conference as per journal guidelines.',
              },
            ].map((step, idx) => (
              <div className={styles.reviewCard} key={idx}>
                <h3>
                  {step.number} {step.title}
                </h3>
                <p style={{ textAlign: 'justify' }}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Important Notes */}
        <div className={styles.importantNotes}>
          <h2 className={styles.sectionTitle}>
            <AlertTriangle size={24} />
            Important Notes
          </h2>
          <div className={styles.notesList}>
            {[
              {
                text: (
                  <>
                    <strong>Deadline:</strong> All submissions must be received by
                    November 20, 2026, 11:59 PM IST. No extensions will be granted.
                  </>
                ),
              },
              {
                text: (
                  <>
                    <strong>Plagiarism:</strong> All papers will be checked for
                    plagiarism. Submissions with significant similarity to existing works
                    will be rejected.
                  </>
                ),
              },
              {
                text: (
                  <>
                    <strong>Author Registration:</strong> At least one author of each accepted paper must register for the conference.
                  </>
                ),
              },
              {
                text: (
                  <>
                    <strong>Presentation:</strong> Accepted papers should be presented at the conference (Hybrid mode, online via Google Meet/Zoom.).
                  </>
                ),
              },
            ].map((note, index) => (
              <div className={styles.noteItem} key={index}>
                <AlertTriangle size={16} />
                <p>{note.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
